import { useState } from "react";
import { useTypewriter } from "@/components/fx/Motion";
import { ShareButton } from "./Share";
import { ChevronDown, Clock, Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { errorMessage } from "@/lib/api";
import { formatSecs } from "@/lib/format";
import { cleanText, download } from "@/lib/script";
import { cn } from "@/lib/utils";
import CopyButton from "./CopyButton";
import StarRating from "./StarRating";

export const ScoreBadge = ({ score, className, testid = "score-badge" }) => (
  <span data-testid={testid} className={cn("inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-ink-800 px-3 py-1 text-xs font-semibold tabular-nums text-white", className)}>
    <span className={cn("h-1.5 w-1.5 rounded-full", score >= 85 ? "bg-brand-volt" : "bg-brand-cyan")} />{score ?? "–"}<span className="font-sans font-normal text-slate-400">/100</span>
  </span>
);

function DownloadMenu({ jobId }) {
  const [busy, setBusy] = useState(false);
  const run = async (params, name) => {
    setBusy(true);
    try { await download(`/export/script/${jobId}`, params, name); } catch (e) { toast.error(errorMessage(e, "Download failed.")); }
    setBusy(false);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="download-menu-btn" className="btn-ghost h-10 text-sm">
        {busy ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : <Download className="h-4 w-4 shrink-0" />} <span className="truncate">Download</span> <ChevronDown className="hidden h-3.5 w-3.5 sm:block" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 rounded-2xl border-white/10 bg-ink-800">
        <DropdownMenuItem data-testid="download-pdf" onClick={() => run({ format: "pdf" }, "script.pdf")} className="cursor-pointer rounded-xl focus:bg-brand/15"><FileText /> Script PDF</DropdownMenuItem>
        <DropdownMenuItem data-testid="download-txt" onClick={() => run({ format: "txt" }, "script.txt")} className="cursor-pointer rounded-xl focus:bg-brand/15"><FileText /> Script TXT</DropdownMenuItem>
        <DropdownMenuItem data-testid="download-srt" onClick={() => run({ format: "srt" }, "captions.srt")} className="cursor-pointer rounded-xl focus:bg-brand/15"><FileText /> Captions SRT</DropdownMenuItem>
        <DropdownMenuItem data-testid="download-brief" onClick={() => run({ format: "brief" }, "editor-brief.pdf")} className="cursor-pointer rounded-xl focus:bg-brand/15"><FileText /> Editor brief PDF</DropdownMenuItem>
        <DropdownMenuItem data-testid="download-report" onClick={() => run({ format: "pdf", full: true }, "report.pdf")} className="cursor-pointer rounded-xl focus:bg-brand/15"><FileText /> Full report PDF</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const sideLabel = (r, beats, i) => (r.platform === "x_thread" ? `Post ${i + 1}` : i === 0 || beats[i - 1].label !== beats[i].label ? beats[i].label : "");

function Line({ text, label, i, animate, delay }) {
  const { shown, typing } = useTypewriter(text, animate, delay);
  return (
    <div data-testid={`script-block-${i}`} data-label={label} className="grid grid-cols-[52px_1fr] gap-3 sm:grid-cols-[64px_1fr]">
      <span className="pt-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-brand-hover/80">{label}</span>
      <p className={cn("whitespace-pre-line text-[16px] leading-[1.75] text-slate-50 sm:text-lg", typing && "caret")}>{shown}</p>
    </div>
  );
}

export function WhyItWorks({ items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-6 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="why-it-works">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">Why it works</p>
      <ul className="mt-2 space-y-1.5 text-sm text-slate-300">{items.map((w, i) => <li key={i} className="flex gap-2"><span className="text-brand">•</span>{w}</li>)}</ul>
    </div>
  );
}

export default function ClientScript({ job, fresh, share }) {
  const r = job.result;
  const beats = r.script.beats;
  const delays = beats.reduce((acc, b, i) => [...acc, i ? acc[i - 1] + Math.min(2200, (beats[i - 1].voiceover.length / 90) * 1000) + 120 : 0], []);
  return (
    <section data-testid="client-script" className="panel !rounded-[28px] p-4 sm:p-8">
      <div className="flex flex-wrap items-center gap-2">
        <ScoreBadge score={r.viral_score.overall} />
        {r.estimated_duration_seconds && <span className="inline-flex items-center gap-1 text-xs text-slate-400" data-testid="script-duration"><Clock className="h-3.5 w-3.5" /> ~{formatSecs(r.estimated_duration_seconds)}</span>}
        <span className="ml-auto"><StarRating jobId={job.id} initial={job.rating || 0} /></span>
      </div>
      <div className="mt-5 space-y-4" data-testid="script-blocks">
        {beats.map((b, i) => <Line key={i} text={b.voiceover} label={sideLabel(r, beats, i)} i={i} animate={fresh && !job.preview} delay={delays[i]} />)}
      </div>
      <WhyItWorks items={r.why_it_works} />
      {r.structure_explainer && <p className="mt-3 text-xs text-slate-500" data-testid="structure-used">Structure used: <span className="font-semibold text-slate-300">{r.structure_name}</span> · {r.structure_explainer}</p>}
      <div className="mt-7 grid grid-cols-[1fr_1.3fr_1fr] gap-1.5 sm:flex sm:flex-wrap sm:gap-2 [&>*]:min-w-0 [&>*]:!gap-1.5 [&>*]:whitespace-nowrap [&>*]:!px-2 [&>*]:text-[13px] sm:[&>*]:!px-5 sm:[&>*]:text-sm">
        <CopyButton text={() => cleanText(r)} label="Copy" variant="brand" testid="copy-script-btn" className="h-10 w-full sm:w-auto" />
        <DownloadMenu jobId={job.id} />
        <ShareButton jobId={job.id} share={share} />
      </div>
    </section>
  );
}
